import { isEmpty, isNil } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { Menu } from '@shared/components/Menu';
import { PullButton } from '@shared/components/PullButton';
import { View } from '@shared/components/View';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { RenderItem } from '@shared/components/filter/filterOptions/MobileTagOptions';
import {
  ControlOption,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
} from '@shared/components/filter/types';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextBody1Bold, MBTextBody2 } from '@shared/components/typography';
import { getFilterComponent } from '@shared/data/cards/CardsEntityService';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { uuid } from '@shared/util/uuid';

interface OffsetOptionsMobileProps {
  offset: API.CreateRecurrenceOffset | null;
  setOffset: (offset: API.CreateRecurrenceOffset | null) => void;
  tagFilter: API.FilterByComponentFragment | undefined;
  setTagFilter: (tagFilter: API.FilterByComponentFragment | undefined) => void;
  workflowTemplateId: string;
}

export function OffsetOptionsMobile({
  offset,
  setOffset,
  tagFilter,
  setTagFilter,
  workflowTemplateId,
}: OffsetOptionsMobileProps) {
  const updateOffset = (key: keyof API.CreateRecurrenceOffset, value: any) => {
    setOffset({
      ...(offset ?? { value: 1, unit: 'DAY' }),
      [key]: value,
    });
  };

  const hasTagFilter = !isNil(tagFilter) && !isEmpty(tagFilter.value?.containsString?.values);

  return (
    <View paddingTop={1.5}>
      <TagFilterMobile
        tagFilter={tagFilter}
        setTagFilter={setTagFilter}
        workflowTemplateId={workflowTemplateId}
      />
      {hasTagFilter && (
        <View marginTop={2}>
          <View marginBottom={1}>
            <MBTextBody1Bold>
              {i18n('workflowTemplate.component.dateAndRepeat.offset.createAnotherCardIn')}
            </MBTextBody1Bold>
          </View>
          <View row gap={1}>
            <MBTextInput
              value={String(offset?.value ?? '')}
              onChangeText={val => updateOffset('value', Number(val))}
              style={{
                width: grid(7),
                paddingHorizontal: grid(1),
                backgroundColor: Colors.v2.backgroundGrayLight,
                borderWidth: 0,
              }}
            />
            <FrequencyMenuMobile
              unit={offset?.unit}
              setUnit={val => updateOffset('unit', val)}
              value={offset?.value}
            />
          </View>
        </View>
      )}
    </View>
  );
}

const unitLabels: Record<API.RecurrenceOffsetUnit, { singular: string; plural: string }> = {
  DAY: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.day.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.day.plural'),
  },
  WEEK: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.week.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.week.plural'),
  },
  MONTH: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.month.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.month.plural'),
  },
  YEAR: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.year.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.year.plural'),
  },
};

const OFFSET_UNITS: API.RecurrenceOffsetUnit[] = ['DAY', 'WEEK', 'MONTH', 'YEAR'];

export function FrequencyMenuMobile({
  unit,
  setUnit,
  value,
}: {
  unit: API.RecurrenceOffsetUnit | undefined;
  setUnit: (unit: API.RecurrenceOffsetUnit) => void;
  value: number | undefined;
}) {
  const isSingular = value === 1;
  const label = isSingular ? unitLabels[unit ?? 'DAY'].singular : unitLabels[unit ?? 'DAY'].plural;

  return (
    <Menu closeOnSelect>
      <Menu.Button flex label={label} />
      <Menu.Content>
        <Menu.Options
          options={OFFSET_UNITS}
          onSelect={setUnit}
          getItemKey={option => option}
          getItemLabel={option =>
            isSingular ? unitLabels[option].singular : unitLabels[option].plural
          }
        />
      </Menu.Content>
    </Menu>
  );
}

function TagFilterMobile({
  tagFilter,
  setTagFilter,
  workflowTemplateId,
}: {
  tagFilter?: API.FilterByComponentFragment;
  setTagFilter: (tagFilter?: API.FilterByComponentFragment) => void;
  workflowTemplateId: string;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const tagComponents = useMemo(
    () => (workflowTemplate?.components ?? []).filter(isTagComponent) as API.TagComponent[],
    [workflowTemplate],
  );

  const tagComponentOptions: ControlOption[] = tagComponents.map(tc => ({
    id: tc.id,
    label: tc.label ?? '',
    icon: tc.icon as MBIconV2Name,
  }));

  const selectedTagComponentOption = tagComponentOptions.find(o => o.id === tagFilter?.componentId);
  const selectedTagComponent = useMemo(() => {
    return workflowTemplate?.components.find(c => c.id === tagFilter?.componentId) as
      | API.TagComponent
      | undefined;
  }, [workflowTemplate, tagFilter]);

  const filterUuidRef = useRef<string>(uuid());

  // Validation state for component dropdown (tag component selection)
  const [componentDropdownError, setComponentDropdownError] = useState(false);
  const componentDropdownOpenedRef = useRef(false);
  const componentHasValidSelectionRef = useRef(false);

  // Validation state for tagOptions dropdown (tag value selection)
  const [tagOptionsDropdownError, setTagOptionsDropdownError] = useState(false);
  const tagOptionsDropdownOpenedRef = useRef(false);
  const tagOptionsHasValidSelectionRef = useRef(false);

  const handleComponentDropdownOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      componentDropdownOpenedRef.current = true;
      // Initialize based on current state when opening
      componentHasValidSelectionRef.current = !isNil(selectedTagComponentOption);
    } else if (componentDropdownOpenedRef.current && !componentHasValidSelectionRef.current) {
      setComponentDropdownError(true);
    }
  };

  const handleTagOptionsDropdownOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      tagOptionsDropdownOpenedRef.current = true;
      // Initialize based on current state when opening
      tagOptionsHasValidSelectionRef.current = !isEmpty(tagFilter?.value?.containsString?.values);
    } else if (tagOptionsDropdownOpenedRef.current && !tagOptionsHasValidSelectionRef.current) {
      setTagOptionsDropdownError(true);
    }
  };

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }

    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label ?? '',
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    };
  }, [selectedTagComponent]);

  // Get the currently selected tag values
  const selectedTagValues = useMemo(
    () => tagFilter?.value?.containsString?.values ?? [],
    [tagFilter?.value?.containsString?.values],
  );
  const selectedTagOptions = useMemo(() => {
    if (isEmpty(selectedTagValues) || !selectedTagComponent) {
      return [];
    }
    return selectedTagValues
      .map(value => selectedTagComponent.options?.find(o => o.value === value))
      .filter(Boolean) as API.TagComponentOption[];
  }, [selectedTagValues, selectedTagComponent]);

  // Available tag options for selection
  const availableTagOptions = useMemo(() => {
    return (selectedTagComponent?.options ?? []).map(o => ({
      value: o.value,
      label: o.label ?? '',
      backgroundColor: o.backgroundColor,
      archived: o.archived ?? undefined,
    }));
  }, [selectedTagComponent]);

  const handleTagValueSelect = (tagValue: string) => {
    if (!filterOption || !selectedTagComponent) {
      return;
    }

    const currentValues = tagFilter?.value?.containsString?.values ?? [];
    const isAlreadySelected = currentValues.includes(tagValue);

    if (isAlreadySelected) {
      // Toggle off - remove the value
      const newValues = currentValues.filter(v => v !== tagValue);

      if (newValues.length === 0) {
        // All values cleared - mark for error on close
        tagOptionsHasValidSelectionRef.current = false;
        setTagFilter({
          componentId: selectedTagComponent.id,
          value: {
            containsString: null,
            isEmpty: null,
            stringWildcard: null,
            containsNumber: null,
            dateIs: null,
            dateRange: null,
            dateTokenIs: null,
            numberIs: null,
            containsCurrentUser: null,
          },
        } as API.FilterByComponentFragment);
      } else {
        // Still have values - update with remaining
        tagOptionsHasValidSelectionRef.current = true;
        setTagOptionsDropdownError(false);
        const newFilterState: FilterSelectionState = {
          filter: filterOption,
          selection: newValues.map(v => ({ value: v })) as FilterSelection,
          negate: true,
        };
        const newTagFilter = getFilterComponent(newFilterState);
        if (newTagFilter) {
          setTagFilter(newTagFilter);
        }
      }
    } else {
      // Adding a new selection
      tagOptionsHasValidSelectionRef.current = true;
      setTagOptionsDropdownError(false);

      const isMultiSelect = selectedTagComponent.multiselect;
      const newValues = isMultiSelect ? [...currentValues, tagValue] : [tagValue];

      const newFilterState: FilterSelectionState = {
        filter: filterOption,
        selection: newValues.map(v => ({ value: v })) as FilterSelection,
        negate: true,
      };

      const newTagFilter = getFilterComponent(newFilterState);
      if (newTagFilter) {
        setTagFilter(newTagFilter);
      }
    }
  };

  const handleTagComponentOptionChange = (option: ControlOption) => {
    // Also reset tagOptions dropdown state since we're changing the component
    setTagOptionsDropdownError(false);
    tagOptionsDropdownOpenedRef.current = false;
    tagOptionsHasValidSelectionRef.current = false;

    if (option.id === tagFilter?.componentId) {
      // Mark that selection is being cleared so error shows on close
      componentHasValidSelectionRef.current = false;
      setTagFilter(undefined);
      return;
    }

    // Mark that a valid selection was made and clear error
    componentHasValidSelectionRef.current = true;
    setComponentDropdownError(false);

    const newFilter: API.FilterByComponentFragment = {
      componentId: option.id,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    } as API.FilterByComponentFragment;

    setTagFilter(newFilter);
  };

  return (
    <View>
      <View marginBottom={1}>
        <MBTextBody1Bold>
          {i18n('workflowTemplate.component.dateAndRepeat.offset.whenTheField')}
        </MBTextBody1Bold>
      </View>
      <PullButton
        onOpenChange={handleComponentDropdownOpenChange}
        renderSelected={() => (
          <View row centerVertically>
            {selectedTagComponentOption?.icon && (
              <MBIconV2
                name={selectedTagComponentOption.icon as MBIconV2Name}
                color={Colors.v2.purple}
                size={grid(3)}
                style={{ marginRight: grid(1) }}
              />
            )}
            <MBTextBody1
              numberOfLines={1}
              color={selectedTagComponentOption ? Colors.v2.purple : Colors.v2.gray50}
            >
              {selectedTagComponentOption?.label ?? i18n('card.actions.groups.noneSelected')}
            </MBTextBody1>
          </View>
        )}
        renderOptions={onSelect => (
          <View>
            {tagComponentOptions.map(option => (
              <TouchableOpacity
                key={option.id}
                onPress={() => {
                  handleTagComponentOptionChange(option);
                  onSelect();
                }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: grid(1.5),
                }}
              >
                <View row centerVertically>
                  {option.icon && (
                    <MBIconV2
                      name={option.icon as MBIconV2Name}
                      color={Colors.v2.purple}
                      size={grid(3)}
                      style={{ marginRight: grid(1) }}
                    />
                  )}
                  <MBTextBody1>{option.label}</MBTextBody1>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}
      />
      {componentDropdownError && (
        <View marginTop={0.5}>
          <MBTextBody2 color={Colors.v2.red}>{i18n('card.form.errors.required')}</MBTextBody2>
        </View>
      )}
      {filterOption && (
        <>
          <View marginBottom={1} marginTop={2}>
            <MBTextBody1Bold>
              {selectedTagComponent?.multiselect && selectedTagOptions.length > 1
                ? i18n('workflowTemplate.component.dateAndRepeat.offset.updatesToOneOf')
                : i18n('workflowTemplate.component.dateAndRepeat.offset.updatesTo')}
            </MBTextBody1Bold>
          </View>
          <PullButton
            isMultiSelect={selectedTagComponent?.multiselect ?? false}
            onOpenChange={handleTagOptionsDropdownOpenChange}
            renderSelected={() => (
              <View row wrap centerVertically>
                {!isEmpty(selectedTagOptions) ? (
                  selectedTagOptions.map((tagOption, index) => (
                    <Tag
                      key={tagOption.value}
                      color={tagOption.backgroundColor as FilterTagOption['backgroundColor']}
                      label={tagOption.label ?? ''}
                      archived={tagOption.archived ?? undefined}
                      style={{
                        maxWidth: grid(24),
                        marginRight: index < selectedTagOptions.length - 1 ? grid(0.5) : 0,
                      }}
                    />
                  ))
                ) : (
                  <MBTextBody1 color={Colors.v2.gray50}>
                    {i18n('card.actions.groups.noneSelected')}
                  </MBTextBody1>
                )}
              </View>
            )}
            renderOptions={onSelect => (
              <View>
                {availableTagOptions.map(option => {
                  const isSelected = selectedTagValues.includes(option.value);
                  return (
                    <TouchableOpacity
                      key={option.value}
                      onPress={() => {
                        handleTagValueSelect(option.value);
                        onSelect();
                      }}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingVertical: grid(1.5),
                      }}
                    >
                      <RenderItem
                        data={{
                          ...option,
                          backgroundColor:
                            option.backgroundColor as FilterTagOption['backgroundColor'],
                        }}
                      />
                      {isSelected && (
                        <MBIconV2 name="check" color={Colors.v2.green} size={grid(2.5)} />
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
          />
          {tagOptionsDropdownError && (
            <View marginTop={0.5}>
              <MBTextBody2 color={Colors.v2.red}>{i18n('card.form.errors.required')}</MBTextBody2>
            </View>
          )}
        </>
      )}
    </View>
  );
}
